import type {
  ICellInfoReturn,
  ITableBodyCell,
} from '../../../../types/data_table';
import type { TPaymentMethod } from '../../../../types/table/table_row/table_cell';

import { getValueFromNestedObject as getValue } from '../../../../utils/getValueFromNestedObject';

import { TableCell } from '../../../Table/TableCell';

import { buildCellProps, resolveCellType } from '../cellProps';
import { getCellInfo } from '../cellValue';

/**
 * One column's cell for one row.
 *
 * Rendering it is a three-step pipeline, and this component is only the last
 * step — picking the `TableCell` to draw:
 *
 * 1. `cellValue.ts` reads the value out of the row and formats it.
 * 2. `cellProps.ts` decides which `TableCell` variant renders that value.
 * 3. here, that variant is rendered — or, for the list-shaped values those two
 *    steps deliberately leave alone, one of the two list cells below.
 */
export const TableBodyCell = ({
  column,
  row,
  rowIndex,
  columnIndex,
  lang,
}: ITableBodyCell) => {
  // A custom column skips the pipeline entirely and renders whatever the
  // consumer's `render` returns.
  if (column.type === 'custom' && column.render) {
    return (
      <TableCell
        type="custom"
        customContent={column.render(
          getValue(row, column.key),
          row,
          columnIndex
        )}
        width={column.width}
      />
    );
  }

  const cellInfo: ICellInfoReturn = getCellInfo(
    {
      type: column.type,
      obj: row,
      key: column.key,
      additionalText: column.additionalText,
      hintText: column.hintText,
      avatar: column.avatar,
      includeTime: column.includeTime,
      format: column.format,
    },
    lang
  );

  // The two column types whose value is a list get their own cell.
  if (column.type === 'payment_method' && Array.isArray(cellInfo)) {
    return (
      <TableCell
        type="leading_payment_method"
        paymentMethods={cellInfo as TPaymentMethod[]}
        width={column.width}
      />
    );
  }

  if (column.type === 'dropdown' && Array.isArray(cellInfo)) {
    return (
      <TableCell
        type="dropdown"
        dropdownMenuItemsProps={cellInfo as any}
        additionalData={row}
        width={column.width}
      />
    );
  }

  // Everything else is a normal cell. There is deliberately no branch that
  // renders nothing: a column that skipped a cell would shift every later cell
  // in that row and break the table's alignment, so an unrenderable value
  // becomes an empty cell rather than no cell.
  return (
    <TableCell
      width={column.width}
      {...buildCellProps(
        column,
        row,
        rowIndex,
        resolveCellType(column, row),
        cellInfo
      )}
    />
  );
};
